import { formatName } from "@api";
import type { IContact } from "@bbj-types/contact";
import type { ObjectId } from "@bbj-types/utils";
import {
  Autocomplete,
  type AutocompleteProps,
  Divider,
  Stack,
  TextField,
  type TextFieldProps,
  Typography,
  createFilterOptions,
} from "@mui/material";

export type TContactAutocomplete<AllowNew extends boolean | undefined = undefined> = (AllowNew extends true
  ? { _id?: ObjectId }
  : { _id: ObjectId }) & { email: string; firstName?: string; lastName?: string; place?: IContact["place"] };

export const isValidContactAutocomplete = <AllowNew extends boolean>(
  contact: Partial<IContact>,
  allowNew: boolean
): contact is TContactAutocomplete<AllowNew> => {
  return Boolean(contact.email) && (Boolean(contact._id) || allowNew);
};

type TAutocompleteContactsProps<
  Option extends TContactAutocomplete<AllowNew>,
  AllowNew extends boolean | undefined = undefined,
> = Omit<
  AutocompleteProps<Option, false, false, false>,
  "filterOptions" | "renderOption" | "isOptionEqualToValue" | "renderInput"
> & {
  allowNew?: AllowNew;
  TextFieldProps?: TextFieldProps;
};

export const AutocompleteContacts = <
  Option extends TContactAutocomplete<AllowNew>,
  AllowNew extends boolean | undefined = undefined,
>({
  allowNew = false,
  TextFieldProps,
  getOptionLabel,
  ...props
}: TAutocompleteContactsProps<Option, AllowNew>) => {
  const filter = createFilterOptions<Option>();

  return (
    <Autocomplete
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (allowNew && params.inputValue !== "" && !options.some((option) => option.email === params.inputValue)) {
          filtered.push({
            email: params.inputValue,
          } as Option);
        }
        return filtered;
      }}
      renderOption={(props, option) =>
        option._id ? (
          <Typography component="li" {...props} key={option._id || option.email} variant="body1">
            {getOptionLabel
              ? getOptionLabel(option)
              : option.firstName || option.lastName
                ? formatName(option)
                : option.email}
          </Typography>
        ) : (
          <Stack
            component="li"
            key="new"
            sx={{
              mt: 1,
              gap: 1,
            }}
          >
            <Divider sx={{ mx: 2 }} />
            <Typography {...props} variant="body1SemiBold" color="primary" sx={{ alignSelf: "stretch" }}>
              + Ajouter comme nouveau contact
            </Typography>
          </Stack>
        )
      }
      getOptionLabel={(option) =>
        getOptionLabel
          ? getOptionLabel(option)
          : option.firstName || option.lastName
            ? formatName(option)
            : option.email
      }
      isOptionEqualToValue={(a, b) => a?._id === b?._id}
      renderInput={(params) => <TextField {...params} size="small" fullWidth {...TextFieldProps} />}
      {...props}
    />
  );
};
